package one.healthium.onboarding.ui.screens

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.fastForEach
import androidx.compose.ui.util.fastForEachIndexed
import androidx.compose.ui.util.fastRoundToInt
import loco.ONBOARDING_WELLNESS_PROFILE_BMI_GRAPH_LABEL
import loco.ONBOARDING_WELLNESS_PROFILE_YOUR_BMI_FORMAT
import one.healthium.domain.common.BMIRange
import one.healthium.domain.user.models.bmi
import one.healthium.domain.user.models.bmiRange
import one.healthium.onboarding.domain.configs.WellnessProfileBmiInfo
import one.healthium.onboarding.domain.configs.WellnessProfileV2ScreenConfig
import one.healthium.onboarding.ui.UserDataProvider
import one.healthium.onboarding.ui.UserProfileUiModel
import one.healthium.onboarding.ui.bindPlaceholderValues
import one.healthium.onboarding.ui.components.HighlightItem
import one.healthium.ui.components.CtaButton
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppText
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.black25
import one.healthium.ui.compose.theme.black45
import one.healthium.ui.compose.theme.gray_10
import one.healthium.ui.compose.theme.gray_3
import one.healthium.ui.compose.theme.white
import one.healthium.ui.compose.theme.yellow_6
import org.jetbrains.compose.resources.stringResource
import kotlin.math.max
import kotlin.math.min

interface WellnessProfileV2EventDelegate {
    fun onNextClicked()
}

@Composable
internal fun WellnessProfileV2Screen(
    eventDelegate: WellnessProfileV2EventDelegate,
    screenConfig: WellnessProfileV2ScreenConfig,
) {
    UserDataProvider { userState, _ ->
        AppScaffold {
            WellnessProfileV2ScreenContent(
                screenConfig = screenConfig,
                userState = userState,
                onNextClicked = eventDelegate::onNextClicked,
            )
        }
    }
}

@Composable
internal fun WellnessProfileV2ScreenContent(
    modifier: Modifier = Modifier,
    screenConfig: WellnessProfileV2ScreenConfig,
    userState: UserProfileUiModel,
    onNextClicked: () -> Unit,
) {
    Column(
        modifier = modifier
            .fillMaxSize()
            .statusBarsPadding()
            .navigationBarsPadding()
            .padding(horizontal = 20.dp)
            .verticalScroll(rememberScrollState()),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        val currentBmi = userState.model.bmi
        val currentBmiRange = userState.model.bmiRange

        AppText(
            text = screenConfig.infos[currentBmiRange]?.title?.bindPlaceholderValues(userState),
            style = AppTheme.textStyles.header,
            color = AppTheme.colors.text100,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(24.dp))
        if (currentBmi != null && currentBmiRange != null) {
            BmiInfoGraph(
                currentBmi = currentBmi,
                currentRange = currentBmiRange,
                bmiInfo = screenConfig.infos[currentBmiRange]
            )
        }
        Spacer(Modifier.height(24.dp))
        screenConfig.items.fastForEach { highlightItem ->
            HighlightItem(
                text = highlightItem.text.bindPlaceholderValues(userState),
                emoji = highlightItem.emoji.value(userState.gender),
                emojiHeight = screenConfig.emojiHeight ?: 40,
                textStyle = screenConfig.textLabel
            )
        }
        Spacer(Modifier.height(24.dp))
        Spacer(Modifier.weight(1f))
        CtaButton(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 14.dp),
            text = screenConfig.ctaTitle,
            enabled = userState.age != null,
            onClick = onNextClicked,
        )
        Spacer(Modifier.height(24.dp))
    }
}

private val shape = RoundedCornerShape(size = 12.dp)

@Composable
private fun BmiInfoGraph(currentBmi: Int, currentRange: BMIRange, bmiInfo: WellnessProfileBmiInfo?) {
    Box {
        Column(
            modifier = Modifier.fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Spacer(Modifier.height(12.dp))
            Box(
                Modifier
                    .border(4.dp, yellow_6, shape)
                    .padding(top = 27.dp, bottom = 24.dp, start = 4.dp, end = 4.dp),
            ) {
                BmiGraph(currentBmi, currentRange)
            }
            Spacer(Modifier.height(24.dp))
            BmiInfo(bmiInfo)
        }
        Text(
            modifier = Modifier
                .align(Alignment.TopCenter)
                .background(yellow_6, CircleShape)
                .padding(horizontal = 12.dp, vertical = 6.dp),
            text = stringResource(loco.Res.string.ONBOARDING_WELLNESS_PROFILE_BMI_GRAPH_LABEL),
            style = AppTheme.textStyles.captionSemiBold,
            color = gray_10,
        )
    }
}

private val dotSize = 28.dp

@Composable
@Preview(showBackground = true)
private fun BmiGraph(currentBmi: Int, currentRange: BMIRange) {
    val parentSize = remember { mutableStateOf(0) }
    val density = LocalDensity.current
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .onGloballyPositioned {
                parentSize.value = it.size.width
            },
        contentAlignment = Alignment.BottomStart
    ) {

        val availableSize = parentSize.value.toFloat() - with(density) { dotSize.toPx() }
        val part = availableSize / 10

        val dotOffset = when (currentBmi) {
            in 0..10 -> 0f
            in 10..20 -> ((currentBmi - 10) / 10f) * (part * 4)
            in 21..26 -> ((currentBmi - 20) / 6f) * (part * 3) + part * 4
            in 27..32 -> ((currentBmi - 26) / 6f) * (part * 3) + part * 4 + part * 3
            else -> availableSize
        }

        val animatedProgress by animateFloatAsState(
            targetValue = dotOffset,
            animationSpec = tween(durationMillis = 800, delayMillis = 550),
        )

        Graph(currentRange)
        Dot(
            Modifier
                .offset {
                    IntOffset(
                        x = max(0f, animatedProgress).fastRoundToInt(),
                        y = 0,
                    )
                }
                .padding(bottom = 14.dp)
        )
        Tooltip(
            modifier = Modifier.padding(bottom = 56.dp),
            dotOffset = { max(0f, animatedProgress) },
            availableSpace = parentSize.value,
            currentBmi = currentBmi,
        )
    }
}

private val graphSize = 8.dp

@Composable
private fun Graph(currentRange: BMIRange) {
    Column(
        Modifier.padding(horizontal = (dotSize - graphSize) / 2)
    ) {
        Row {
            BMIRange.entries.fastForEachIndexed { index, bmiRange ->
                if (index > 0) {
                    when (bmiRange) {
                        BMIRange.Underweight -> {}
                        BMIRange.Normal -> Spacer(Modifier.weight(3.9f))
                        BMIRange.Overweight -> Spacer(Modifier.weight(2.9f))
                        BMIRange.Obese -> Spacer(Modifier.weight(3f))
                    }
                }
                Text(
                    text = when (bmiRange) {
                        BMIRange.Underweight -> "10"
                        BMIRange.Normal -> "20"
                        BMIRange.Overweight -> "26"
                        BMIRange.Obese -> "32"
                    },
                    style = AppTheme.textStyles.default10Medium,
                    color = black25,
                )
            }
        }
        Spacer(Modifier.height(8.dp))
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(graphSize)
                .background(
                    Brush.linearGradient(
                        0f to Color(0xFF00BDFF),
                        0.28f to Color(0xFF00DB58),
                        0.56f to Color(0xFFFFD704),
                        0.81f to Color(0xFFFFB800),
                        1f to Color(0xFFDA4960),
                    ),
                    RoundedCornerShape(100)
                )
        )
        Spacer(Modifier.height(12.dp))
        Row {
            BMIRange.entries.fastForEachIndexed { index, bmiRange ->
                if (index > 0) {
                    Spacer(Modifier.weight(1f))
                }
                Text(
                    text = bmiRange.name.uppercase(),
                    style = if (currentRange == bmiRange) AppTheme.textStyles.default10ExtraBold else AppTheme.textStyles.default10Medium,
                    color = if (currentRange == bmiRange) gray_10 else black45,
                )
            }
        }
    }
}

@Composable
private fun Dot(
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = modifier,
        contentAlignment = Alignment.Center,
    ) {
        Box(
            Modifier
                .size(dotSize)
                .background(gray_3, RoundedCornerShape(100))
        )
        Box(
            Modifier
                .size(12.dp)
                .background(white, RoundedCornerShape(100))
        )
    }
}

@Composable
private fun Tooltip(
    modifier: Modifier = Modifier,
    dotOffset: () -> Float,
    availableSpace: Int,
    currentBmi: Int,
) {
    val tooltipSize = remember { mutableStateOf(IntSize.Zero) }
    Box(modifier) {
        Box(
            modifier = Modifier
                .onGloballyPositioned {
                    tooltipSize.value = it.size
                }
                .offset {
                    val availableSpaceTooltip = availableSpace - tooltipSize.value.width
                    val tooltipOffset = dotOffset() + dotSize.div(2).toPx() - tooltipSize.value.width.div(2)
                    IntOffset(
                        x = min(availableSpaceTooltip, max(0, tooltipOffset.fastRoundToInt())),
                        y = 0,
                    )
                }
                .drawBehind {
                    drawRoundRect(
                        color = gray_3,
                        cornerRadius = CornerRadius(x = 6.dp.toPx(), y = 6.dp.toPx())
                    )
                    drawTriangle(gray_3)
                }
                .padding(6.dp)
        ) {
            Text(
                text = stringResource(loco.Res.string.ONBOARDING_WELLNESS_PROFILE_YOUR_BMI_FORMAT, currentBmi),
                style = AppTheme.textStyles.captionBold,
                color = white,
            )
        }
        Box(
            modifier = Modifier
                .offset {
                    IntOffset(
                        x = (dotOffset() + dotSize.div(2).toPx()).fastRoundToInt(),
                        y = tooltipSize.value.height - 2,
                    )
                }
                .drawBehind {
                    drawTriangle(gray_3)
                }
        )
    }
}

private fun DrawScope.drawTriangle(color: Color) {
    val path = androidx.compose.ui.graphics.Path().apply {
        moveTo(size.width / 2 - 8.dp.toPx(), 0f)
        lineTo(size.width / 2 + 8.dp.toPx(), 0f)
        lineTo(size.width / 2, 12.dp.toPx())
        close()
    }

    drawPath(
        path = path,
        color = color,
    )
}

@Composable
private fun BmiInfo(info: WellnessProfileBmiInfo?) {
    if (info == null) return

    info.text?.let {
        Text(
            text = it,
            style = AppTheme.textStyles.caption,
            color = AppTheme.colors.text100,
            textAlign = TextAlign.Center,
        )
    }
}